import {
  asRecordingId,
  type RecordingId,
} from "@repo/app-types/entities/recording";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  buildTranscriptSnippetParam,
  parseTranscriptSnippetParam,
  type TranscriptSnippetLocation,
} from "@repo/app-types/helpers/transcriptSnippetLocation";
import { buildAppUrl } from "../../../helpers/appUrls.js";

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

export function estimateCharacterOffsets(
  selectionText: string,
  startText: string,
  endText: string,
  isSameFragment: boolean,
): {
  start_offset: number | undefined;
  end_offset: number | undefined;
} {
  // For single-fragment selections, we can just use the index of the selection in the fragment
  if (isSameFragment) {
    const trimmed = selectionText.trim();
    if (!trimmed) return { start_offset: undefined, end_offset: undefined };

    const index = startText.indexOf(trimmed);
    if (index === -1) return { start_offset: undefined, end_offset: undefined };

    return {
      start_offset: index,
      end_offset: Math.min(startText.length, index + trimmed.length),
    };
  }

  // For cross-fragment selections, we need to find the longest matching prefix and suffix in the start and end fragments
  const trimmed = selectionText.trim();
  if (!trimmed) return { start_offset: undefined, end_offset: undefined };

  // Find longest matching prefix in start fragment
  let so: number | undefined;
  for (let len = trimmed.length; len > 0; len--) {
    const prefix = trimmed.slice(0, len);
    const idx = startText.indexOf(prefix);
    if (idx !== -1) {
      so = idx;
      break;
    }
  }

  // Find longest matching suffix in end fragment
  let eo: number | undefined;
  for (let len = trimmed.length; len > 0; len--) {
    const suffix = trimmed.slice(-len);
    const idx = endText.lastIndexOf(suffix);
    if (idx !== -1) {
      eo = Math.min(endText.length, idx + suffix.length);
      break;
    }
  }

  return { start_offset: so, end_offset: eo };
}

export function calculateTimeFromCharacterOffsets(
  recording: AccountGradingRecording,
  startFragmentIndex: number,
  endFragmentIndex: number,
  startCharOffset?: number,
  endCharOffset?: number,
): { startSec: number; endSec: number } {
  const fragments = recording.transcript?.fragments ?? [];

  const startFragment = fragments[startFragmentIndex];
  const endFragment = fragments[endFragmentIndex];

  if (!startFragment || !endFragment) {
    return { startSec: 0, endSec: 0 };
  }

  const startFraction =
    startCharOffset !== undefined
      ? clamp(
          startCharOffset / (startFragment.text_transcript.length || 1),
          0,
          1,
        )
      : 0;

  const endFraction =
    endCharOffset !== undefined
      ? clamp(endCharOffset / (endFragment.text_transcript.length || 1), 0, 1)
      : 1;

  // Convert to seconds
  const startMs =
    startFragment.start_time +
    startFraction * (startFragment.end_time - startFragment.start_time);
  const endMs =
    endFragment.start_time +
    endFraction * (endFragment.end_time - endFragment.start_time);

  return {
    startSec: startMs / 1000,
    endSec: endMs / 1000,
  };
}

export function createSnippetUrl(location: TranscriptSnippetLocation): string {
  const { pathname, search, hash } = window.location;
  const url = new URL(buildAppUrl(`/link${pathname}${search}${hash}`));
  url.searchParams.set("snippet", buildTranscriptSnippetParam(location));
  return url.toString();
}

/** Parses the `snippet` query parameter; checks nothing against transcripts. */
export function maybeGetShareSnippetFromUrl():
  | TranscriptSnippetLocation
  | undefined {
  if (typeof window === "undefined") return undefined;
  const snippetParam = new URLSearchParams(window.location.search).get(
    "snippet",
  );
  if (!snippetParam) return undefined;
  return parseTranscriptSnippetParam(snippetParam);
}

export function findFragmentData(
  node: Node | null,
  container: HTMLElement,
): { recordingId: RecordingId; fragmentIndex: number } | null {
  let current: Node | null = node;

  while (current && current !== container) {
    if (
      current instanceof HTMLElement &&
      current.hasAttribute("data-recording-id") &&
      current.hasAttribute("data-fragment-index")
    ) {
      const recordingId = current.getAttribute("data-recording-id");
      const fragmentIndex = Number(current.getAttribute("data-fragment-index"));

      if (!recordingId || !Number.isFinite(fragmentIndex)) {
        return null;
      }

      return { recordingId: asRecordingId(recordingId), fragmentIndex };
    }
    current = current.parentNode;
  }

  return null;
}
